import React, { type JSX } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Fact rendering callback type
type RenderFactBadge = (factId: string, key: string) => React.ReactNode;

/** Wraps rendered children so inline [[FACT:id]] placeholders become badges. */
type WrapChildren = (children: React.ReactNode, key?: string) => React.ReactNode;

interface ChatMarkdownRendererProps {
  content: string;
  /** Optional callback to render fact badges. If provided, [[FACT:id]] placeholders will be replaced with badges. */
  renderFactBadge?: RenderFactBadge;
}

const FACT_PATTERN_SOURCE = /\[\[FACT:([^\]]+)\]\]|\[\[FACT_LOADING(?::\d+)?\]\]/.source;

function factLoadingBadge(key: string): React.ReactNode {
  return (
    <span
      key={key}
      className="inline-flex items-center justify-center w-5 h-5 mx-0.5 text-xs bg-gray-200 text-gray-400 rounded-full animate-pulse"
    >
      …
    </span>
  );
}

/**
 * Split a text node on fact placeholders, interleaving the surrounding text with
 * badges. Returns the text untouched when it contains no placeholders.
 */
function replaceFactPlaceholders(
  text: string,
  renderFactBadge: RenderFactBadge,
  keyBase: string
): React.ReactNode {
  // A fresh regex per call — the global flag makes `lastIndex` stateful.
  const factPattern = new RegExp(FACT_PATTERN_SOURCE, 'g');
  if (!factPattern.test(text)) {
    return text;
  }
  factPattern.lastIndex = 0;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let partKey = 0;

  while ((match = factPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match[0].startsWith('[[FACT_LOADING')) {
      parts.push(factLoadingBadge(`${keyBase}-loading-${partKey++}`));
    } else {
      parts.push(renderFactBadge(match[1], `${keyBase}-fact-${partKey++}`));
    }

    lastIndex = factPattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <>{parts}</>;
}

/**
 * Process React children to replace [[FACT:id]] placeholders with fact badge components.
 * This preserves the original markdown structure while injecting inline fact badges.
 */
function processChildrenForFacts(
  children: React.ReactNode,
  renderFactBadge: RenderFactBadge,
  keyPrefix: string = ''
): React.ReactNode {
  return React.Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      return replaceFactPlaceholders(child, renderFactBadge, `${keyPrefix}-${index}`);
    }

    // If it's a React element, recursively process its children
    if (React.isValidElement(child)) {
      const childElement = child as React.ReactElement<{ children?: React.ReactNode }>;
      if (childElement.props.children) {
        return React.cloneElement(childElement, {
          ...childElement.props,
          children: processChildrenForFacts(
            childElement.props.children,
            renderFactBadge,
            `${keyPrefix}-${index}`
          ),
        });
      }
    }

    return child;
  });
}

// ─── Markdown component overrides ─────────────────────────────
// Headers render as divs with ARIA heading roles so chat bubbles keep their
// own typographic scale without introducing document-level headings.

function headingComponents(wrap: WrapChildren): Components {
  return {
    h1: ({ children }) => (
      <div
        role="heading"
        aria-level={1}
        className="block text-lg font-bold text-cvc-text mt-3 mb-2 first:mt-0"
      >
        {wrap(children, 'h1')}
      </div>
    ),
    h2: ({ children }) => (
      <div
        role="heading"
        aria-level={2}
        className="block text-base font-bold text-cvc-text mt-2 mb-1.5"
      >
        {wrap(children, 'h2')}
      </div>
    ),
    h3: ({ children }) => (
      <div
        role="heading"
        aria-level={3}
        className="block text-sm font-bold text-cvc-text mt-2 mb-1"
      >
        {wrap(children, 'h3')}
      </div>
    ),
  };
}

function textComponents(wrap: WrapChildren): Components {
  return {
    p: ({ children }) => <p className="block mb-2 last:mb-0">{wrap(children, 'p')}</p>,
    ul: ({ children }) => (
      <ul className="block list-disc list-outside ml-5 mb-2 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="block list-decimal list-outside ml-5 mb-2 space-y-1">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{wrap(children, 'li')}</li>,
    strong: ({ children }) => (
      <strong className="font-semibold">{wrap(children, 'strong')}</strong>
    ),
    em: ({ children }) => <em className="italic">{wrap(children, 'em')}</em>,
  };
}

function codeComponents(): Components {
  return {
    code: ({ children, className }) => {
      // Check if this is a code block (has language class) vs inline code
      const isCodeBlock = className && className.startsWith('language-');
      if (isCodeBlock) {
        return (
          <code className="block bg-gray-100 text-gray-800 px-3 py-2 rounded-sm text-xs font-mono overflow-x-auto my-2">
            {children}
          </code>
        );
      }
      return (
        <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded-sm text-xs font-mono">
          {children}
        </code>
      );
    },
    pre: ({ children }) => <div className="block my-2">{children}</div>,
    blockquote: ({ children }) => (
      <blockquote className="block border-l-4 border-gray-300 pl-3 my-2 italic text-gray-600">
        {children}
      </blockquote>
    ),
  };
}

function linkComponents(): Components {
  return {
    a: ({ href, children }) => {
      const safeHref =
        typeof href === 'string' && /^https?:\/\//i.test(href) ? href : undefined;

      if (!safeHref) {
        // Render non-clickable styled text if URL is not a safe http/https link
        return <span className="text-cvc-blue">{children}</span>;
      }

      return (
        <a
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cvc-blue hover:underline"
        >
          {children}
        </a>
      );
    },
    hr: () => <hr className="block border-t border-gray-200 my-3" />,
  };
}

// Fallback table styling — the main tables are handled by MarkdownTable.
function tableComponents(wrap: WrapChildren): Components {
  return {
    table: ({ children }) => (
      <div className="block overflow-x-auto my-2">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th className="px-2 py-1 text-left font-semibold text-gray-700 border-b border-gray-300">
        {wrap(children, 'th')}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-2 py-1 text-gray-600 border-b border-gray-100">{wrap(children, 'td')}</td>
    ),
  };
}

function buildMarkdownComponents(wrap: WrapChildren): Components {
  return {
    ...headingComponents(wrap),
    ...textComponents(wrap),
    ...codeComponents(),
    ...linkComponents(),
    ...tableComponents(wrap),
  };
}

/**
 * ChatMarkdownRenderer - A ReactMarkdown wrapper optimized for chat UI
 * Renders GitHub-Flavored Markdown with styling appropriate for inline chat messages.
 * Uses React.memo to prevent unnecessary re-renders during streaming.
 *
 * When renderFactBadge is provided, [[FACT:id]] placeholders in the text are automatically
 * replaced with inline fact badge components, preserving the markdown structure.
 */
function ChatMarkdownRendererComponent({
  content,
  renderFactBadge,
}: ChatMarkdownRendererProps): JSX.Element {
  if (!content || !content.trim()) {
    return <>{content}</>;
  }

  const wrapChildren: WrapChildren = (children, key = 'root') => {
    if (renderFactBadge) {
      return processChildrenForFacts(children, renderFactBadge, key);
    }
    return children;
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={buildMarkdownComponents(wrapChildren)}>
      {content}
    </ReactMarkdown>
  );
}

// Memoize to prevent re-renders during streaming when content hasn't changed
const ChatMarkdownRenderer = React.memo(ChatMarkdownRendererComponent);

export default ChatMarkdownRenderer;
